import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  FaArrowLeft, FaUser, FaTooth, FaClipboardList, FaClipboardCheck, FaImages,
  FaCalendarAlt, FaFlask, FaFileSignature, FaExclamationTriangle, FaSyncAlt,
  FaCheck, FaTimes, FaClock, FaLock, FaLockOpen, FaStickyNote, FaInfoCircle,
  FaGraduationCap, FaSearch, FaChevronRight, FaHistory, FaFilePdf
} from 'react-icons/fa';
import jsPDF from 'jspdf';
// The arch layout, tooth sizing and surface diagram are the student's own
// stylesheet, so the faculty view is the same drawing they made.
import '../student/Chart.css';
import './PatientDossier.css';

/* Odenta palette, shared with the rest of the supervisor area. */
const C = {
  primary: '#0077B6',
  secondary: '#20B2AA',
  ink: '#0C2432',
  muted: '#5F7F91'
};

/* Fallback vocabulary. The API sends the authoritative copy in `chartLegend`
   so the supervisor's colours can never drift from the student's chart. */
const FALLBACK_LEGEND = {
  upperTeeth: [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28],
  lowerTeeth: [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38],
  procedures: [], conditions: []
};

const STATUS_STYLES = {
  accepted:  'bg-green-100 text-green-700 border-green-200',
  approved:  'bg-green-100 text-green-700 border-green-200',
  completed: 'bg-blue-100 text-blue-700 border-blue-200',
  declined:  'bg-red-100 text-red-700 border-red-200',
  rejected:  'bg-red-100 text-red-700 border-red-200',
  cancelled: 'bg-gray-100 text-gray-600 border-gray-200',
  pending:   'bg-amber-100 text-amber-700 border-amber-200'
};

const TIMELINE_META = {
  registration:     { icon: FaUser,            tint: 'bg-slate-100 text-slate-600' },
  procedureRequest: { icon: FaClipboardList,   tint: 'bg-teal-100 text-teal-700' },
  appointment:      { icon: FaCalendarAlt,     tint: 'bg-sky-100 text-sky-700' },
  chart:            { icon: FaTooth,           tint: 'bg-blue-100 text-blue-700' },
  sheet:            { icon: FaClipboardList,   tint: 'bg-indigo-100 text-indigo-700' },
  labRequest:       { icon: FaFlask,           tint: 'bg-teal-100 text-teal-700' },
  reviewSubmitted:  { icon: FaClipboardCheck,  tint: 'bg-amber-100 text-amber-700' },
  reviewDecided:    { icon: FaClipboardCheck,  tint: 'bg-green-100 text-green-700' }
};

/* ------------------------------------------------------------------ helpers */

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};
const titleCase = (value) =>
  String(value || '').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ')
    .replace(/^./, c => c.toUpperCase());

/* ------------------------------------------------------------ PDF export
   Carried over from the dashboard's old inline patient panel, which these
   buttons replace. The original printed `tooth.treatment`, a field the chart
   never writes, so every row read "Treatment: N/A"; it reads `procedure` here
   and adds the marked surfaces. */

const PDF = { blue: [0, 119, 182], grey: [110, 110, 110], black: [0, 0, 0], faint: [150, 150, 150] };

const pdfHeader = (doc, title, subtitle, patient) => {
  const w = doc.internal.pageSize.getWidth();
  let y = 20;
  doc.setFontSize(18);
  doc.setTextColor(...PDF.blue);
  doc.text(title, w / 2, y, { align: 'center' });
  y += 8;
  if (subtitle) {
    doc.setFontSize(11);
    doc.setTextColor(...PDF.grey);
    doc.text(subtitle, w / 2, y, { align: 'center' });
    y += 8;
  }
  doc.setDrawColor(...PDF.blue);
  doc.line(15, y, w - 15, y);
  y += 9;

  const pair = (label, value, x, labelW) => {
    doc.setFontSize(10);
    doc.setTextColor(...PDF.grey);
    doc.text(label, x, y);
    doc.setTextColor(...PDF.black);
    doc.text(String(value || 'N/A'), x + labelW, y);
  };
  pair('Patient:', patient.fullName, 15, 22);
  pair('National ID:', patient.nationalId, 110, 26);
  y += 7;
  pair('Serial:', patient.serialNumber, 15, 22);
  pair('Printed:', new Date().toLocaleString('en-GB'), 110, 26);
  return y + 11;
};

const pdfFooter = (doc) => {
  const w = doc.internal.pageSize.getWidth();
  doc.setFontSize(8);
  doc.setTextColor(...PDF.faint);
  doc.text('Odenta - faculty review copy', w / 2, 290, { align: 'center' });
};

const exportChartPdf = (chart, patient) => {
  const doc = new jsPDF();
  const w = doc.internal.pageSize.getWidth();
  let y = pdfHeader(doc, 'Dental Chart', chart.title, patient);

  doc.setFontSize(10);
  doc.setTextColor(...PDF.grey);
  doc.text(
    `Recorded by ${chart.studentName || 'unknown student'} · updated ${fmtDate(chart.updatedAt || chart.date)} · ` +
    `${chart.isLocked ? 'locked' : 'open for editing'}`, 15, y);
  y += 6;
  doc.text(
    `${chart.summary.recorded} teeth recorded (${chart.summary.upper} upper, ${chart.summary.lower} lower) · ` +
    `${chart.summary.surfacesMarked} surfaces marked`, 15, y);
  y += 12;

  const recorded = (chart.teeth || []).filter(t => t.hasData);
  doc.setFontSize(13);
  doc.setTextColor(...PDF.blue);
  doc.text(`Teeth recorded (${recorded.length})`, 15, y);
  y += 9;

  if (!recorded.length) {
    doc.setFontSize(10);
    doc.setTextColor(...PDF.grey);
    doc.text('Nothing has been recorded on this chart.', 15, y);
  } else {
    recorded.forEach(tooth => {
      const hasNote = Boolean(tooth.notes);
      const blockH = hasNote ? 27 : 20;
      if (y + blockH > 275) { doc.addPage(); y = 20; }

      doc.setFillColor(248, 250, 252);
      doc.roundedRect(15, y - 5, w - 30, blockH, 2, 2, 'F');

      doc.setFontSize(10);
      doc.setTextColor(...PDF.blue);
      doc.setFont(undefined, 'bold');
      doc.text(`Tooth ${tooth.toothNumber}`, 20, y + 1);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(...PDF.grey);
      doc.text(tooth.arch, 45, y + 1);

      doc.setTextColor(...PDF.black);
      doc.text(`Procedure: ${tooth.procedure || '-'}`, 20, y + 8);
      doc.text(`Condition: ${tooth.condition || '-'}`, 105, y + 8);
      doc.text(`Surfaces: ${tooth.surfaces.length ? tooth.surfaces.join(', ') : '-'}`, 20, y + 14);

      if (hasNote) {
        doc.setTextColor(...PDF.grey);
        doc.text(doc.splitTextToSize(`Note: ${tooth.notes}`, w - 45), 20, y + 20);
      }
      y += blockH + 4;
    });
  }

  pdfFooter(doc);
  doc.save(`dental-chart-${patient.nationalId}-${chart.id}.pdf`);
};

const exportSheetPdf = (sheet, patient) => {
  const doc = new jsPDF();
  const w = doc.internal.pageSize.getWidth();
  let y = pdfHeader(doc, 'Examination Sheet',
    `${sheet.sheetType}${sheet.subType ? ` - ${sheet.subType}` : ''}`, patient);

  doc.setFontSize(10);
  doc.setTextColor(...PDF.grey);
  doc.text(`Filled by ${sheet.studentName || 'unknown student'} · ${fmtDate(sheet.updatedAt || sheet.createdAt)}`, 15, y);
  y += 12;

  const SKIP = new Set(['id', '_id', 'sheetType', 'type', 'subType', 'studentId', 'studentName',
    'createdAt', 'updatedAt', 'patientId', 'nationalId', 'data', 'procedureType']);

  const flatten = (obj, prefix = '') => Object.entries(obj || {}).flatMap(([key, value]) => {
    if (SKIP.has(key) || value === null || value === undefined || value === '' || value === 'N/A') return [];
    const label = prefix ? `${prefix} - ${titleCase(key)}` : titleCase(key);
    if (typeof value === 'boolean') return [[label, value ? 'Yes' : 'No']];
    if (Array.isArray(value)) {
      return value.length ? [[label, value.filter(v => typeof v !== 'object').join(', ') || `${value.length} entries`]] : [];
    }
    if (typeof value === 'object') return flatten(value, label);
    return [[label, String(value)]];
  });

  const rows = flatten(sheet.data);
  if (!rows.length) {
    doc.setTextColor(...PDF.grey);
    doc.text('This sheet has no filled fields.', 15, y);
  } else {
    rows.forEach(([label, value]) => {
      const lines = doc.splitTextToSize(value, w - 95);
      const blockH = Math.max(7, lines.length * 5 + 2);
      if (y + blockH > 275) { doc.addPage(); y = 20; }
      doc.setFontSize(9.5);
      doc.setTextColor(...PDF.grey);
      doc.text(doc.splitTextToSize(label, 68), 15, y);
      doc.setTextColor(...PDF.black);
      doc.text(lines, 88, y);
      y += blockH;
    });
  }

  pdfFooter(doc);
  doc.save(`examination-sheet-${patient.nationalId}-${sheet.id}.pdf`);
};

const PdfButton = ({ onClick, label = 'PDF' }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
  >
    <FaFilePdf className="h-3 w-3 text-red-600" /> {label}
  </button>
);

const StatusPill = ({ status, children }) => {
  const key = String(status || 'pending').toLowerCase();
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLES[key] || STATUS_STYLES.pending}`}>
      {children || key}
    </span>
  );
};

const Card = ({ title, icon: Icon, action, children, className = '' }) => (
  <section className={`bg-white rounded-xl border border-gray-200 shadow-sm ${className}`}>
    {title && (
      <header className="flex items-center gap-2 px-4 sm:px-5 py-3 border-b border-gray-100">
        {Icon && <Icon className="h-4 w-4 flex-shrink-0" style={{ color: C.primary }} />}
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {action && <div className="ml-auto">{action}</div>}
      </header>
    )}
    <div className="p-4 sm:p-5">{children}</div>
  </section>
);

const Field = ({ label, value, wide }) => (
  <div className={wide ? 'sm:col-span-2' : ''}>
    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
    <dd className="mt-0.5 text-sm text-gray-900 break-words">{value || <span className="text-gray-400">Not recorded</span>}</dd>
  </div>
);

const Empty = ({ icon: Icon, title, hint }) => (
  <div className="text-center py-10 px-4">
    {Icon && <Icon className="h-9 w-9 mx-auto text-gray-300 mb-3" />}
    <p className="text-sm font-medium text-gray-900">{title}</p>
    {hint && <p className="mt-1 text-sm text-gray-500">{hint}</p>}
  </div>
);

const Stat = ({ label, value, tone }) => (
  <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
    <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</div>
    <div className="text-lg font-bold tabular-nums" style={{ color: tone || C.ink }}>{value}</div>
  </div>
);

/* ------------------------------------------------------- tooth chart (as the
   student sees it: the same tooth images, the same FDI arches, the same
   surface diagram and the same colours) */

const ToothSurfaces = ({ arch, tooth }) => {
  const surfaces = tooth?.surfaces || [];
  const color = tooth?.color;

  // The student's chart labels the centre square by arch alone - incisal on
  // upper teeth, occlusal on lower - and the palatal/lingual face the same way.
  // That mislabels an upper molar, so a stored 'occlusal' on tooth 16 would not
  // light up. Each face here accepts either name so nothing the student marked
  // is invisible to the reviewer; the detail panel still lists the stored names.
  const fill = (...names) =>
    (color && names.some(name => surfaces.includes(name)) ? color : 'white');

  return (
    <div className="tooth-surfaces">
      <svg width="80" height="80" viewBox="0 0 300 300" style={{ maxWidth: '100%', height: 'auto' }} aria-hidden="true">
        <rect x="75" y="75" width="150" height="150" stroke="black" strokeWidth="5"
          fill={fill('occlusal', 'incisal')} />
        <polygon points="0 0 300 0 225 75 75 75" stroke="black" strokeWidth="5" fill={fill('buccal')} />
        <polygon points="300 0 300 300 225 225 225 75" stroke="black" strokeWidth="5" fill={fill('mesial')} />
        <polygon points="300 300 0 300 75 225 225 225" stroke="black" strokeWidth="5"
          fill={fill('palatal', 'lingual')} />
        <polygon points="0 300 0 0 75 75 75 225" stroke="black" strokeWidth="5" fill={fill('distal')} />
      </svg>
    </div>
  );
};

const ToothView = ({ number, arch, tooth, selected, onSelect }) => {
  const recorded = Boolean(tooth?.hasData);
  const label = recorded
    ? `Tooth ${number}: ${[tooth.procedure, tooth.condition].filter(Boolean).join(', ') || 'surfaces marked'}`
    : `Tooth ${number}: nothing recorded`;

  return (
    <div className={`tooth-container ${arch}`}>
      <ToothSurfaces tooth={tooth} />
      <button
        type="button"
        title={label}
        aria-label={label}
        aria-pressed={selected}
        onClick={() => onSelect(recorded ? number : null)}
        className={`tooth ${arch} ${selected ? 'selected' : ''} dossier-tooth ${recorded ? 'is-recorded' : ''}`}
        style={recorded && tooth.color ? { '--tooth-accent': tooth.color } : undefined}
      >
        <img
          src={`${process.env.PUBLIC_URL}/imgs/teeth/${number}.png`}
          alt=""
          className="tooth-image"
          loading="lazy"
        />
        {/* The student's chart shows colour only on marked surfaces, so a tooth
            with a procedure but no surface would read as blank. This dot makes
            recorded teeth findable without changing what the colour means. */}
        {recorded && tooth.color && <span className="indicator" style={{ backgroundColor: tooth.color }} />}
        {recorded && tooth.notes && <FaStickyNote className="dossier-tooth-note" />}
        <span className="tooth-number">{number}</span>
      </button>
    </div>
  );
};

const ToothDetail = ({ tooth, onClose }) => {
  if (!tooth) return null;
  return (
    <div className="rounded-lg border-2 bg-white p-4" style={{ borderColor: tooth.color || '#E5E7EB' }}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="h-9 w-9 rounded-lg grid place-items-center text-sm font-bold text-white"
            style={{ backgroundColor: tooth.color || C.muted }}>
            {tooth.toothNumber}
          </span>
          <div>
            <p className="text-sm font-semibold text-gray-900">Tooth {tooth.toothNumber}</p>
            <p className="text-xs text-gray-500 capitalize">{tooth.arch} arch</p>
          </div>
        </div>
        <button onClick={onClose} className="p-1.5 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700" aria-label="Close tooth detail">
          <FaTimes className="h-3.5 w-3.5" />
        </button>
      </div>
      <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Procedure" value={tooth.procedure} />
        <Field label="Condition" value={tooth.condition} />
        <Field
          label={`Surfaces marked (${tooth.surfaces.length})`}
          value={tooth.surfaces.length
            ? <span className="flex flex-wrap gap-1">
                {tooth.surfaces.map(s => (
                  <span key={s} className="rounded bg-gray-100 px-1.5 py-0.5 text-xs capitalize text-gray-700">{s}</span>
                ))}
              </span>
            : null}
          wide
        />
        <Field label="Student's note" value={tooth.notes} wide />
      </dl>
    </div>
  );
};

const ToothChartPanel = ({ charts, legend, patient }) => {
  const [chartId, setChartId] = useState(charts[0]?.id || null);
  const [selectedTooth, setSelectedTooth] = useState(null);
  const [onlyRecorded, setOnlyRecorded] = useState(false);

  const chart = charts.find(c => c.id === chartId) || charts[0];

  const byNumber = useMemo(() => {
    const map = new Map();
    (chart?.teeth || []).forEach(t => map.set(t.toothNumber, t));
    return map;
  }, [chart]);

  if (!chart) {
    return (
      <Card title="Dental chart" icon={FaTooth}>
        <Empty icon={FaTooth} title="No dental chart yet"
          hint="A chart is created the first time the student opens the tooth chart for this patient." />
      </Card>
    );
  }

  const rows = (chart.teeth || []).filter(t => !onlyRecorded || t.hasData);
  const usedProcedures = new Set(chart.summary.procedures.map(p => p.name));
  const usedConditions = new Set(chart.summary.conditions.map(c => c.name));

  return (
    <div className="space-y-4">
      <Card
        title="Dental chart"
        icon={FaTooth}
        action={
          <div className="flex items-center gap-2">
            {charts.length > 1 && (
              <select
                value={chart.id}
                onChange={(e) => { setChartId(e.target.value); setSelectedTooth(null); }}
                className="rounded-lg border border-gray-300 px-2 py-1 text-xs focus:ring-2 focus:ring-blue-500"
              >
                {charts.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.title} · {fmtDate(c.updatedAt || c.date)}{c.isLocked ? ' · locked' : ''}
                  </option>
                ))}
              </select>
            )}
            <PdfButton onClick={() => exportChartPdf(chart, patient)} label="Export chart" />
          </div>
        }
      >
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-sm font-semibold text-gray-900">{chart.title}</span>
          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${
            chart.isLocked ? 'border-gray-300 bg-gray-100 text-gray-700' : 'border-teal-200 bg-teal-50 text-teal-700'}`}>
            {chart.isLocked ? <FaLock className="h-3 w-3" /> : <FaLockOpen className="h-3 w-3" />}
            {chart.isLocked ? 'Locked' : 'Open for editing'}
          </span>
          <span className="text-xs text-gray-500">
            {chart.studentName ? `by ${chart.studentName}` : 'student not recorded'} · updated {fmtDate(chart.updatedAt || chart.date)}
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-5">
          <Stat label="Teeth recorded" value={chart.summary.recorded} tone={C.primary} />
          <Stat label="Upper" value={chart.summary.upper} />
          <Stat label="Lower" value={chart.summary.lower} />
          <Stat label="Surfaces" value={chart.summary.surfacesMarked} tone={C.secondary} />
          <Stat label="With notes" value={chart.summary.withNotes} />
        </div>

        {chart.summary.recorded === 0 ? (
          <Empty icon={FaTooth} title="The chart exists but nothing has been recorded on it yet"
            hint="The student has opened the chart; no tooth carries a procedure, condition, surface or note." />
        ) : (
          <>
            {chart.summary.unknownToothNumbers.length > 0 && (
              <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                <FaExclamationTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                <span>
                  Tooth {chart.summary.unknownToothNumbers.join(', ')} {chart.summary.unknownToothNumbers.length === 1 ? 'is' : 'are'} outside
                  standard FDI numbering, so {chart.summary.unknownToothNumbers.length === 1 ? 'it is' : 'they are'} not
                  drawn on the arches below. {chart.summary.unknownToothNumbers.length === 1 ? 'It is' : 'They are'} listed in the table.
                </span>
              </div>
            )}

            {/* The same arch layout, tooth images and surface diagram the student uses. */}
            <div className="dossier-chart-scroll">
              <div className="teeth-chart dossier-chart">
                <div className="upper-teeth">
                  {(legend.upperTeeth || FALLBACK_LEGEND.upperTeeth).map(n => (
                    <ToothView key={n} number={n} arch="upper" tooth={byNumber.get(n)}
                      selected={selectedTooth === n} onSelect={setSelectedTooth} />
                  ))}
                </div>
                <div className="lower-teeth">
                  {(legend.lowerTeeth || FALLBACK_LEGEND.lowerTeeth).map(n => (
                    <ToothView key={n} number={n} arch="lower" tooth={byNumber.get(n)}
                      selected={selectedTooth === n} onSelect={setSelectedTooth} />
                  ))}
                </div>
              </div>
            </div>

            <p className="mt-2 text-xs text-gray-500">
              Read-only. Coloured surfaces and the corner dot mark teeth the student recorded — click one for its full entry.
            </p>

            {selectedTooth && byNumber.get(selectedTooth) && (
              <div className="mt-4">
                <ToothDetail tooth={byNumber.get(selectedTooth)} onClose={() => setSelectedTooth(null)} />
              </div>
            )}
          </>
        )}
      </Card>

      {(usedProcedures.size > 0 || usedConditions.size > 0) && (
        <Card title="Legend and totals for this chart" icon={FaInfoCircle}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[['Procedures', chart.summary.procedures, legend.procedures || [], usedProcedures],
              ['Conditions', chart.summary.conditions, legend.conditions || [], usedConditions]].map(([heading, tallies, palette]) => (
              <div key={heading}>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{heading}</h4>
                {tallies.length === 0 ? (
                  <p className="text-sm text-gray-400">None recorded</p>
                ) : (
                  <ul className="space-y-1.5">
                    {tallies.map(item => {
                      const swatch = palette.find(p => p.name === item.name);
                      return (
                        <li key={item.name} className="flex items-center gap-2 text-sm">
                          <span className="h-3 w-3 rounded-sm border border-black/10 flex-shrink-0"
                            style={{ backgroundColor: swatch?.color || '#CBD5E1' }} />
                          <span className="text-gray-800">{item.name}</span>
                          <span className="ml-auto tabular-nums text-gray-500">
                            {item.count} {item.count === 1 ? 'tooth' : 'teeth'}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card
        title={`Every tooth entry (${rows.length})`}
        icon={FaClipboardList}
        action={
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            <input type="checkbox" checked={onlyRecorded} onChange={(e) => setOnlyRecorded(e.target.checked)}
              className="rounded border-gray-300" />
            Recorded only
          </label>
        }
      >
        {rows.length === 0 ? (
          <Empty icon={FaClipboardList} title="No tooth entries on this chart" />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Tooth</th>
                  <th className="px-3 py-2 text-left font-medium">Arch</th>
                  <th className="px-3 py-2 text-left font-medium">Procedure</th>
                  <th className="px-3 py-2 text-left font-medium">Condition</th>
                  <th className="px-3 py-2 text-left font-medium">Surfaces</th>
                  <th className="px-3 py-2 text-left font-medium">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(tooth => (
                  <tr key={tooth.toothNumber} className={tooth.hasData ? '' : 'text-gray-400'}>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: tooth.color || '#E5E7EB' }} />
                        <span className="font-semibold tabular-nums text-gray-900">{tooth.toothNumber}</span>
                      </span>
                    </td>
                    <td className="px-3 py-2 capitalize">{tooth.arch}</td>
                    <td className="px-3 py-2">{tooth.procedure || '—'}</td>
                    <td className="px-3 py-2">{tooth.condition || '—'}</td>
                    <td className="px-3 py-2 capitalize">{tooth.surfaces.join(', ') || '—'}</td>
                    <td className="px-3 py-2 max-w-xs truncate" title={tooth.notes || ''}>{tooth.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

/* -------------------------------------------------------------- review panel */

const ReviewCard = ({ review }) => {
  const { stepSummary: s } = review;
  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50/60">
        <StatusPill status={review.status} />
        <span className="text-sm font-semibold text-gray-900">{review.procedureType}</span>
        <span className="text-xs text-gray-500">
          submitted {fmtDateTime(review.submittedDate)}
          {review.reviewedDate && ` · decided ${fmtDateTime(review.reviewedDate)}`}
        </span>
        <span className="ml-auto text-xs text-gray-500">
          {review.studentName || review.studentId || 'Student not recorded'}
        </span>
      </div>

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Steps" value={s.total} />
          <Stat label="Ticked by student" value={s.completedByStudent} tone={C.primary} />
          <Stat label="Approved" value={s.accepted} tone="#1B8F52" />
          <Stat label="Declined" value={s.declined} tone={s.declined ? '#BF3B41' : undefined} />
        </div>

        {s.total > 0 ? (
          <div className="rounded-lg border border-gray-200 overflow-hidden">
            <div className="grid grid-cols-[1fr,7rem,7rem] gap-2 bg-gray-50 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-gray-500">
              <span>Review step</span>
              <span className="text-center">Student</span>
              <span className="text-center">Faculty</span>
            </div>
            <ul className="divide-y divide-gray-100">
              {review.steps.map(step => (
                <li key={step.index} className="grid grid-cols-[1fr,7rem,7rem] gap-2 items-center px-3 py-2">
                  <span className="text-sm text-gray-800">
                    {step.label}
                    {step.note && <span className="block text-xs text-gray-500">{step.note}</span>}
                  </span>
                  <span className="text-center">
                    {step.completedByStudent === true ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                        <FaCheck className="h-3 w-3" /> Done
                      </span>
                    ) : step.completedByStudent === false ? (
                      <span className="text-xs text-gray-400">Not ticked</span>
                    ) : (
                      <span className="text-xs text-gray-300" title="This submission stored steps without tick information">—</span>
                    )}
                  </span>
                  <span className="text-center">
                    {step.supervisorDecision === 'accepted' ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                        <FaCheck className="h-3 w-3" /> Approved
                      </span>
                    ) : step.supervisorDecision === 'declined' ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700">
                        <FaTimes className="h-3 w-3" /> Declined
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                        <FaClock className="h-3 w-3" /> Pending
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-sm text-gray-500">This submission recorded no review steps.</p>
        )}

        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Student's comment" value={review.studentComment} wide />
          {review.studentNote && <Field label="Student's note" value={review.studentNote} wide />}
          <Field label="Reviewed by" value={review.supervisorName} />
          <Field
            label="Signature"
            value={review.hasSupervisorSignature
              ? <span className="inline-flex items-center gap-1 text-green-700"><FaCheck className="h-3 w-3" /> Recorded</span>
              : null}
          />
        </dl>
      </div>
    </div>
  );
};

/* --------------------------------------------------------------- sheet panel */

const SheetBody = ({ data }) => {
  // Sheet contents differ per discipline, so the body is rendered from whatever
  // keys it holds rather than a fixed field list that would silently hide new
  // ones. Bookkeeping keys are dropped.
  const SKIP = new Set(['id', '_id', 'sheetType', 'type', 'subType', 'studentId', 'studentName',
    'createdAt', 'updatedAt', 'patientId', 'nationalId', 'data', 'procedureType']);

  const entries = Object.entries(data || {}).filter(([key, value]) => {
    if (SKIP.has(key)) return false;
    if (value === null || value === undefined || value === '' || value === 'N/A') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return false;
    return true;
  });

  if (!entries.length) return <p className="text-sm text-gray-500">This sheet has no filled fields.</p>;

  const render = (value) => {
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) {
      return value.every(v => typeof v !== 'object')
        ? value.join(', ')
        : <pre className="whitespace-pre-wrap text-xs text-gray-700">{JSON.stringify(value, null, 2)}</pre>;
    }
    if (typeof value === 'object') {
      const nested = Object.entries(value).filter(([, v]) => v !== null && v !== '' && v !== 'N/A');
      if (!nested.length) return '—';
      return (
        <ul className="space-y-0.5">
          {nested.map(([k, v]) => (
            <li key={k} className="text-sm">
              <span className="text-gray-500">{titleCase(k)}:</span>{' '}
              <span className="text-gray-900">{typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)}</span>
            </li>
          ))}
        </ul>
      );
    }
    return String(value);
  };

  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
      {entries.map(([key, value]) => (
        <Field key={key} label={titleCase(key)} value={render(value)}
          wide={typeof value === 'object' || String(value).length > 80} />
      ))}
    </dl>
  );
};

/* ----------------------------------------------------------------- main page */

const TABS = [
  { id: 'overview', label: 'Overview',   icon: FaUser },
  { id: 'chart',    label: 'Tooth chart', icon: FaTooth,          count: d => d.counts.charts },
  { id: 'sheets',   label: 'Sheets',      icon: FaClipboardList,  count: d => d.counts.sheets },
  { id: 'reviews',  label: 'Reviews',     icon: FaClipboardCheck, count: d => d.counts.reviews },
  { id: 'imaging',  label: 'Imaging',     icon: FaImages,         count: d => d.counts.xrays + d.counts.gallery },
  { id: 'schedule', label: 'Schedule',    icon: FaCalendarAlt,    count: d => d.counts.appointments + d.counts.procedureRequests },
  { id: 'lab',      label: 'Lab',         icon: FaFlask,          count: d => d.counts.labRequests },
  { id: 'consent',  label: 'Consent',     icon: FaFileSignature }
];

const SupervisorPatientInfo = () => {
  const { nationalId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('overview');
  const [lightbox, setLightbox] = useState(null);
  const [reviewFilter, setReviewFilter] = useState('all');
  const [sheetQuery, setSheetQuery] = useState('');

  const load = useCallback(async ({ silent } = {}) => {
    if (silent) setRefreshing(true); else setLoading(true);
    setError('');
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/supervisors/patient/${encodeURIComponent(nationalId)}/dossier`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDossier(res.data);
    } catch (err) {
      console.error('Error loading patient dossier:', err);
      setError(err.response?.status === 404
        ? 'No patient found with this national ID.'
        : err.response?.data?.message || 'Could not load this patient. Check your connection and try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [nationalId, token]);

  useEffect(() => { if (nationalId && token) load(); }, [nationalId, token, load]);

  const failedSources = useMemo(
    () => Object.entries(dossier?.sources || {}).filter(([, s]) => s.status === 'error'),
    [dossier]
  );

  const visibleReviews = useMemo(() => {
    const list = dossier?.reviews || [];
    return reviewFilter === 'all' ? list : list.filter(r => r.status === reviewFilter);
  }, [dossier, reviewFilter]);

  const visibleSheets = useMemo(() => {
    const list = dossier?.sheets || [];
    const needle = sheetQuery.trim().toLowerCase();
    if (!needle) return list;
    return list.filter(s => JSON.stringify(s).toLowerCase().includes(needle));
  }, [dossier, sheetQuery]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2" style={{ borderColor: C.primary }} />
      </div>
    );
  }

  if (error || !dossier) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4">
        <div className="rounded-xl border border-red-200 bg-red-50 p-5">
          <div className="flex items-start gap-3">
            <FaExclamationTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
            <div className="flex-1">
              <h2 className="text-base font-semibold text-red-900">Patient record unavailable</h2>
              <p className="mt-1 text-sm text-red-800">{error}</p>
              <p className="mt-1 text-xs text-red-700 break-all">National ID: {nationalId}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => load()}
                  className="rounded-lg px-3 py-1.5 text-sm font-medium text-white" style={{ backgroundColor: C.primary }}>
                  Try again
                </button>
                <button onClick={() => navigate('/supervisor/reviews')}
                  className="rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700">
                  Back to reviews
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { patient, counts, timeline, contributors, chartLegend } = dossier;
  const legend = chartLegend || FALLBACK_LEGEND;
  const alerts = [
    ...(patient.medical.chronicDiseases || []),
    patient.medical.allergies && `Allergies: ${patient.medical.allergies}`
  ].filter(Boolean);

  return (
    <div className="space-y-5">
      {/* header */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 sm:px-6 py-4" style={{ background: `linear-gradient(135deg, ${C.primary}0F, ${C.secondary}14)` }}>
          <button onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 mb-3">
            <FaArrowLeft className="h-3 w-3" /> Back
          </button>

          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{patient.fullName}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
                <span className="break-all">ID {patient.nationalId}</span>
                {patient.serialNumber && <span>Serial {patient.serialNumber}</span>}
                {patient.age && <span>{patient.age} yrs</span>}
                {patient.gender && <span>{patient.gender}</span>}
                {patient.phoneNumber && <span>{patient.phoneNumber}</span>}
              </div>
              {alerts.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {alerts.map(alert => (
                    <span key={alert} className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                      <FaExclamationTriangle className="h-2.5 w-2.5" /> {alert}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {contributors.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Worked on by</div>
                  <div className="text-sm font-semibold text-gray-900">
                    {contributors.map(c => c.name).join(', ')}
                  </div>
                </div>
              )}
              <button onClick={() => load({ silent: true })} disabled={refreshing}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60">
                <FaSyncAlt className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
              </button>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-2">
            <Stat label="Charts" value={counts.charts} tone={C.primary} />
            <Stat label="Teeth recorded" value={counts.teethRecorded} tone={C.primary} />
            <Stat label="Sheets" value={counts.sheets} />
            <Stat label="Submitted" value={counts.reviews} tone={C.secondary} />
            <Stat label="Pending" value={counts.reviewsPending} tone={counts.reviewsPending ? '#C07A10' : undefined} />
            <Stat label="Images" value={counts.xrays + counts.gallery} />
          </div>
        </div>

        {/* tabs */}
        <nav className="flex gap-1 overflow-x-auto px-2 sm:px-4 border-t border-gray-100" role="tablist">
          {TABS.map(({ id, label, icon: Icon, count }) => {
            const n = count ? count(dossier) : null;
            const active = tab === id;
            return (
              <button
                key={id}
                role="tab"
                aria-selected={active}
                onClick={() => setTab(id)}
                className={`relative flex items-center gap-2 whitespace-nowrap px-3 py-3 text-sm font-medium transition-colors ${
                  active ? '' : 'text-gray-500 hover:text-gray-800'}`}
                style={active ? { color: C.primary } : undefined}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
                {n !== null && (
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${
                    n > 0 ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-400'}`}>{n}</span>
                )}
                {active && <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-t" style={{ backgroundColor: C.primary }} />}
              </button>
            );
          })}
        </nav>
      </div>

      {failedSources.length > 0 && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <FaExclamationTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium">Some sections could not be loaded</p>
            <ul className="mt-1 space-y-0.5 text-xs">
              {failedSources.map(([key, source]) => (
                <li key={key}>{titleCase(key)}: {source.message || 'request failed'}</li>
              ))}
            </ul>
            <p className="mt-1 text-xs">Everything else on this page loaded normally.</p>
          </div>
          <button onClick={() => load({ silent: true })}
            className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-800">
            Retry
          </button>
        </div>
      )}

      <motion.div key={tab} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18 }}>
        {/* ---------------------------------------------------------- overview */}
        {tab === 'overview' && (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="space-y-4 xl:col-span-1">
              <Card title="Patient record" icon={FaUser}>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  <Field label="Full name" value={patient.fullName} wide />
                  <Field label="National ID" value={patient.nationalId} wide />
                  <Field label="Serial number" value={patient.serialNumber} />
                  <Field label="Registered" value={fmtDate(patient.registrationDate)} />
                  <Field label="Age" value={patient.age} />
                  <Field label="Gender" value={patient.gender} />
                  <Field label="Phone" value={patient.phoneNumber} />
                  <Field label="Occupation" value={patient.occupation} />
                  <Field label="Address" value={patient.address} wide />
                  <Field label="Notes" value={patient.notes} wide />
                </dl>
              </Card>

              <Card title="Medical history" icon={FaClipboardList}>
                <dl className="grid grid-cols-1 gap-3">
                  <Field label="Chief complaint" value={patient.medical.chiefComplaint} />
                  <Field
                    label="Chronic diseases"
                    value={patient.medical.chronicDiseases.length
                      ? <span className="flex flex-wrap gap-1">
                          {patient.medical.chronicDiseases.map(d => (
                            <span key={d} className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">{d}</span>
                          ))}
                        </span>
                      : null}
                  />
                  <Field label="Other chronic disease" value={patient.medical.otherChronicDisease} />
                  <Field label="Current medications" value={patient.medical.currentMedications} />
                  <Field label="Recent surgical procedures" value={patient.medical.recentSurgicalProcedures} />
                  <Field label="Allergies" value={patient.medical.allergies} />
                </dl>
              </Card>
            </div>

            <div className="space-y-4 xl:col-span-2">
              <Card title="What the student did for this patient" icon={FaGraduationCap}>
                {contributors.length === 0 ? (
                  <Empty icon={FaGraduationCap} title="No student work recorded for this patient yet" />
                ) : (
                  <div className="space-y-3">
                    {contributors.map(person => (
                      <div key={person.studentId || person.name} className="rounded-lg border border-gray-200 p-3">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="h-8 w-8 rounded-full grid place-items-center text-xs font-semibold text-white"
                            style={{ background: `linear-gradient(135deg, ${C.primary}, ${C.secondary})` }}>
                            {String(person.name || '?').trim().charAt(0).toUpperCase()}
                          </span>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{person.name}</p>
                            {person.studentId && <p className="text-xs text-gray-500">{person.studentId}</p>}
                          </div>
                          {person.isAssigned && (
                            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">Assigned</span>
                          )}
                          {person.studentId && (
                            <Link to="/supervisor/students"
                              className="ml-auto inline-flex items-center gap-1 text-xs font-medium hover:underline"
                              style={{ color: C.primary }}>
                              Scorecard <FaChevronRight className="h-2.5 w-2.5" />
                            </Link>
                          )}
                        </div>
                        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                          <Stat label="Charts" value={person.charts} />
                          <Stat label="Sheets" value={person.sheets} />
                          <Stat label="Reviews" value={person.reviews} />
                          <Stat label="Visits" value={person.appointments} />
                          <Stat label="Lab" value={person.labRequests} />
                          <Stat label="Requests" value={person.procedureRequests} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              <Card title="Review outcomes" icon={FaClipboardCheck}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                  <Stat label="Submitted" value={counts.reviews} tone={C.primary} />
                  <Stat label="Accepted" value={counts.reviewsAccepted} tone={counts.reviewsAccepted ? '#1B8F52' : undefined} />
                  <Stat label="Declined" value={counts.reviewsDeclined} tone={counts.reviewsDeclined ? '#BF3B41' : undefined} />
                  <Stat label="Pending" value={counts.reviewsPending} tone={counts.reviewsPending ? '#C07A10' : undefined} />
                </div>
                {dossier.reviews.length === 0 ? (
                  <Empty icon={FaClipboardCheck} title="Nothing submitted for review on this patient yet" />
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {dossier.reviews.slice(0, 5).map(review => (
                      <li key={review.id} className="flex flex-wrap items-center gap-2 py-2">
                        <StatusPill status={review.status} />
                        <span className="text-sm text-gray-900">{review.procedureType}</span>
                        <span className="text-xs text-gray-500">
                          {review.stepSummary.completedByStudent}/{review.stepSummary.total} steps
                        </span>
                        <span className="ml-auto text-xs text-gray-500">{fmtDate(review.submittedDate)}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {dossier.reviews.length > 5 && (
                  <button onClick={() => setTab('reviews')}
                    className="mt-3 text-xs font-medium hover:underline" style={{ color: C.primary }}>
                    See all {dossier.reviews.length} submissions →
                  </button>
                )}
              </Card>

              <Card title="Activity timeline" icon={FaHistory}>
                {timeline.length === 0 ? (
                  <Empty icon={FaHistory} title="No dated activity recorded" />
                ) : (
                  <ol className="space-y-3">
                    {timeline.map((event, i) => {
                      const meta = TIMELINE_META[event.kind] || { icon: FaInfoCircle, tint: 'bg-gray-100 text-gray-600' };
                      const Icon = meta.icon;
                      return (
                        <li key={`${event.at}-${i}`} className="flex gap-3">
                          <span className={`h-7 w-7 flex-shrink-0 rounded-full grid place-items-center ${meta.tint}`}>
                            <Icon className="h-3 w-3" />
                          </span>
                          <div className="min-w-0 flex-1 pb-1">
                            <div className="flex flex-wrap items-baseline gap-2">
                              <p className="text-sm font-medium text-gray-900">{event.title}</p>
                              {event.status && <StatusPill status={event.status} />}
                              <span className="ml-auto text-xs text-gray-500 whitespace-nowrap">{fmtDateTime(event.at)}</span>
                            </div>
                            {event.detail && <p className="text-xs text-gray-600">{event.detail}</p>}
                            {event.actor && <p className="text-xs text-gray-400">{event.actor}</p>}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </Card>
            </div>
          </div>
        )}

        {/* ------------------------------------------------------------- chart */}
        {tab === 'chart' && <ToothChartPanel charts={dossier.charts} legend={legend} patient={patient} />}

        {/* ------------------------------------------------------------ sheets */}
        {tab === 'sheets' && (
          <div className="space-y-4">
            {dossier.sheets.length > 3 && (
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input
                  type="text" placeholder="Search within sheets…"
                  value={sheetQuery} onChange={(e) => setSheetQuery(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
            {visibleSheets.length === 0 ? (
              <Card><Empty icon={FaClipboardList}
                title={dossier.sheets.length ? 'No sheet matches that search' : 'No examination sheet filled yet'}
                hint={dossier.sheets.length ? null : 'Sheets appear here once the student saves one from the patient profile.'} />
              </Card>
            ) : visibleSheets.map(sheet => (
              <Card
                key={sheet.id}
                title={`${sheet.sheetType}${sheet.subType ? ` · ${sheet.subType}` : ''}`}
                icon={FaClipboardList}
                action={
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500">
                      {sheet.studentName || 'student not recorded'} · {fmtDate(sheet.updatedAt || sheet.createdAt)}
                    </span>
                    <PdfButton onClick={() => exportSheetPdf(sheet, patient)} />
                  </div>
                }
              >
                <SheetBody data={sheet.data} />
              </Card>
            ))}
          </div>
        )}

        {/* ----------------------------------------------------------- reviews */}
        {tab === 'reviews' && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {[['all', 'All', counts.reviews], ['pending', 'Pending', counts.reviewsPending],
                ['accepted', 'Accepted', counts.reviewsAccepted], ['declined', 'Declined', counts.reviewsDeclined]]
                .map(([id, label, n]) => (
                  <button key={id} onClick={() => setReviewFilter(id)}
                    className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                      reviewFilter === id ? 'border-blue-300 bg-blue-50 text-blue-800' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}>
                    {label}<span className="tabular-nums text-xs text-gray-500">{n}</span>
                  </button>
                ))}
            </div>
            {visibleReviews.length === 0 ? (
              <Card><Empty icon={FaClipboardCheck}
                title={counts.reviews ? 'No submission in this state' : 'Nothing submitted for review on this patient yet'} />
              </Card>
            ) : visibleReviews.map(review => <ReviewCard key={review.id} review={review} />)}
          </div>
        )}

        {/* ----------------------------------------------------------- imaging */}
        {tab === 'imaging' && (
          <div className="space-y-4">
            {[['X-rays', patient.xrays], ['Clinical photos', patient.gallery]].map(([heading, items]) => (
              <Card key={heading} title={`${heading} (${items.length})`} icon={FaImages}>
                {items.length === 0 ? (
                  <Empty icon={FaImages} title={`No ${heading.toLowerCase()} uploaded`} />
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {items.map(item => (
                      <button key={item.id} onClick={() => setLightbox(item)}
                        className="group text-left rounded-lg border border-gray-200 overflow-hidden hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <img src={item.url} alt={item.note || heading}
                          className="h-32 w-full object-cover bg-gray-100 transition-transform group-hover:scale-[1.03]" loading="lazy" />
                        <div className="p-2">
                          <p className="text-xs text-gray-700 truncate">{item.note || 'No note'}</p>
                          <p className="text-[11px] text-gray-400">{fmtDate(item.uploadedAt)}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* ---------------------------------------------------------- schedule */}
        {tab === 'schedule' && (
          <div className="space-y-4">
            <Card title={`Appointments (${dossier.appointments.length})`} icon={FaCalendarAlt}>
              {dossier.appointments.length === 0 ? (
                <Empty icon={FaCalendarAlt} title="No appointment recorded for this patient" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Date</th>
                        <th className="px-3 py-2 text-left font-medium">Time</th>
                        <th className="px-3 py-2 text-left font-medium">Type</th>
                        <th className="px-3 py-2 text-left font-medium">Clinic</th>
                        <th className="px-3 py-2 text-left font-medium">Student</th>
                        <th className="px-3 py-2 text-left font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {dossier.appointments.map(a => (
                        <tr key={a.id}>
                          <td className="px-3 py-2 whitespace-nowrap">{fmtDate(a.date)}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{a.time || '—'}</td>
                          <td className="px-3 py-2">{a.type || '—'}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {[a.clinicNumber && `C${a.clinicNumber}`, a.unitNumber && `U${a.unitNumber}`].filter(Boolean).join(' · ') || '—'}
                          </td>
                          <td className="px-3 py-2">{a.studentName || '—'}</td>
                          <td className="px-3 py-2"><StatusPill status={a.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <Card title={`Procedure requests (${dossier.procedureRequests.length})`} icon={FaClipboardList}>
              {dossier.procedureRequests.length === 0 ? (
                <Empty icon={FaClipboardList} title="No procedure request for this patient" />
              ) : (
                <ul className="divide-y divide-gray-100">
                  {dossier.procedureRequests.map(p => (
                    <li key={p.id} className="py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <StatusPill status={p.status} />
                        <span className="text-sm font-medium text-gray-900">{p.procedureType || 'Unspecified'}</span>
                        {p.level && <span className="text-xs text-gray-500">Level {p.level}</span>}
                        <span className="ml-auto text-xs text-gray-500">{fmtDate(p.date || p.createdAt)}</span>
                      </div>
                      <p className="mt-1 text-xs text-gray-600">
                        {[p.studentName, p.clinicNumber && `Clinic ${p.clinicNumber}`, p.unitNumber && `Unit ${p.unitNumber}`, p.time]
                          .filter(Boolean).join(' · ') || '—'}
                      </p>
                      {p.notes && <p className="mt-1 text-xs text-gray-600">Notes: {p.notes}</p>}
                      {p.responseNote && <p className="mt-1 text-xs text-gray-600">Response: {p.responseNote}</p>}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        )}

        {/* --------------------------------------------------------------- lab */}
        {tab === 'lab' && (
          <Card title={`Lab requests (${dossier.labRequests.length})`} icon={FaFlask}>
            {dossier.labRequests.length === 0 ? (
              <Empty icon={FaFlask} title="No lab request for this patient" />
            ) : (
              <ul className="divide-y divide-gray-100">
                {dossier.labRequests.map(l => (
                  <li key={l.id} className="py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusPill status={l.status} />
                      <span className="text-sm font-medium text-gray-900">{l.workType || 'Lab work'}</span>
                      {l.labType && (
                        <span className="rounded-full bg-teal-50 px-2 py-0.5 text-xs font-medium capitalize text-teal-700">{l.labType}</span>
                      )}
                      <span className="ml-auto text-xs text-gray-500">
                        requested {fmtDate(l.createdAt)}{l.dueDate && ` · due ${fmtDate(l.dueDate)}`}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-600">{l.studentName || 'student not recorded'}</p>
                    {l.notes && <p className="mt-1 text-xs text-gray-600">Notes: {l.notes}</p>}
                    {l.responseNote && <p className="mt-1 text-xs text-gray-600">Response: {l.responseNote}</p>}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}

        {/* ----------------------------------------------------------- consent */}
        {tab === 'consent' && (
          <Card title="Consent" icon={FaFileSignature}>
            {!patient.consent ? (
              <Empty icon={FaFileSignature} title="No consent recorded for this patient" />
            ) : (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  {patient.consent.isSigned ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-3 py-1 text-sm font-semibold text-green-700">
                      <FaCheck className="h-3 w-3" /> Signed
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-sm font-semibold text-amber-700">
                      <FaClock className="h-3 w-3" /> Not signed
                    </span>
                  )}
                  {patient.consent.signedAt && (
                    <span className="text-sm text-gray-600">on {fmtDateTime(patient.consent.signedAt)}</span>
                  )}
                </div>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Terms agreed" value={patient.consent.agreedTerms ? 'Yes' : 'No'} />
                  <Field label="Typed signature" value={patient.consent.signatureText} />
                </dl>
                {patient.consent.signatureImage && (
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">Signature</p>
                    <img src={patient.consent.signatureImage} alt="Patient signature"
                      className="max-h-32 rounded-lg border border-gray-200 bg-white p-2" />
                  </div>
                )}
              </div>
            )}
          </Card>
        )}
      </motion.div>

      <p className="text-center text-xs text-gray-400">
        Read-only view · loaded {fmtDateTime(dossier.generatedAt)}
      </p>

      {lightbox && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 p-4" onClick={() => setLightbox(null)}>
          <div className="max-h-full max-w-4xl overflow-auto" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.url} alt={lightbox.note || 'Patient image'} className="max-h-[80vh] w-auto rounded-lg" />
            <div className="mt-2 flex items-start gap-3 rounded-lg bg-white p-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-900">{lightbox.note || 'No note recorded'}</p>
                <p className="text-xs text-gray-500">
                  {fmtDateTime(lightbox.uploadedAt)}
                  {lightbox.uploadedBy && ` · ${lightbox.uploadedBy}`}
                  {lightbox.fileName && ` · ${lightbox.fileName}`}
                </p>
              </div>
              <button onClick={() => setLightbox(null)} className="p-2 rounded text-gray-400 hover:bg-gray-100" aria-label="Close image">
                <FaTimes className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupervisorPatientInfo;
